import { Button } from "@hortis/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@hortis/ui/dialog";
import { toast } from "@hortis/ui/sonner";
import { A, F, pipe } from "@mobily/ts-belt";
import { useQueryClient } from "@tanstack/react-query";
import { namedOperations } from "generated/graphql";
import { updatePlantMaterialsDocument } from "src/features/actions-menu/graphql";
import { useGqlMutation } from "src/utils/hooks/graphql";

interface MakePrivateDialogProps {
  selectedMaterials: ReadonlyArray<{ id: string; public: boolean }>;
  onSuccess: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MakePrivateDialog = ({
  selectedMaterials,
  onSuccess,
  open,
  onOpenChange,
}: MakePrivateDialogProps) => {
  const queryClient = useQueryClient();
  const updatePlantMaterials = useGqlMutation({
    mutation: updatePlantMaterialsDocument,
    onError: () => {
      toast.error("Failed to update materials");
    },
    onSuccess: (res) => {
      if (!res.updatePlantMaterials.success) {
        throw new Error("Failed to update materials");
      }
      toast.success(
        `Successfully updated ${
          res.updatePlantMaterials.plantMaterials.length
        } material${
          res.updatePlantMaterials.plantMaterials.length > 1 ? "s" : ""
        }`,
      );
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.PlantMaterials],
      });
      onSuccess();
    },
  });

  const onSubmit = () => {
    const ids = pipe(
      selectedMaterials,
      A.filter((m) => m.public),
      A.map(({ id }) => id),
      F.toMutable,
    );
    if (ids.length === 0) {
      onSuccess();
      return;
    }
    updatePlantMaterials.mutate({
      ids,
      input: {
        public: false,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-cy="make-private-dialog">
        <DialogHeader>
          <DialogTitle>
            Are you sure you want to make{" "}
            {selectedMaterials.length === 1
              ? "this material"
              : "these materials"}{" "}
            private?
          </DialogTitle>
          <DialogDescription>
            {selectedMaterials.length === 1 ? "It" : "They"} will no longer be
            displayed on your public site.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button size="lg" fullWidth>
              Cancel
            </Button>
          </DialogClose>
          <Button
            size="lg"
            variant="primary"
            loading={updatePlantMaterials.isPending}
            fullWidth
            testId="make-private-confirm"
            onClick={onSubmit}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { MakePrivateDialog };
